import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { colors, fonts, gradients, radius, shadows } from "@/lib/theme";

interface Props {
    onGenerate: () => void;
    /**
     * `stale` is the same screen after the nurse went back and changed an
     * assessment answer. The plan she had is gone on purpose — it was derived
     * from the answer she just corrected — so this says that outright rather
     * than showing her the blank "No Plan of Care yet" screen and leaving her
     * to wonder where her work went.
     */
    variant?: "empty" | "stale";
}

// Wording note: this screen is reached by two different visits — one that will
// have a plan WRITTEN for it, and a general special visit that will have the
// plan already on file loaded for amendment. The old copy promised "our AI will
// analyze the patient assessment", which is false for the second: it has no
// assessment on its form. Rather than fetch a mode just to pick a sentence, the
// copy below is simply true of both.
export function PocEmptyState({ onGenerate, variant = "empty" }: Props) {
    const stale = variant === "stale";
    return (
        <View style={styles.container}>
            {/* Icon container */}
            <LinearGradient
                colors={[...gradients.surface]}
                style={styles.iconContainer}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
            >
                <MaterialCommunityIcons
                    name={stale ? "clipboard-alert-outline" : "clipboard-text-outline"}
                    size={44}
                    color={stale ? colors.warning500 : colors.teal400}
                />
            </LinearGradient>

            {/* Title */}
            <Text style={styles.title}>{stale ? "Assessment changed" : "No Plan of Care yet"}</Text>

            {/* Subtitle */}
            <Text style={styles.subtitle}>
                {stale
                    ? "You changed an answer in the assessment, so the plan of care built on the old answer no longer applies. Generate it again to pick up the change."
                    : "We'll put together this patient's plan of care — duties and frequencies — for you to review, adjust and confirm."}
            </Text>

            {/* Generate button */}
            <TouchableOpacity onPress={onGenerate} activeOpacity={0.85} style={styles.btnWrapper}>
                <LinearGradient
                    colors={[...gradients.brand]}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                    style={styles.generateBtn}
                >
                    <MaterialCommunityIcons
                        name={stale ? "refresh" : "clipboard-text-outline"}
                        size={20}
                        color="#FFFFFF"
                    />
                    <Text style={styles.generateBtnText}>
                        {stale ? "Re-generate Plan of Care" : "Open Plan of Care"}
                    </Text>
                </LinearGradient>
            </TouchableOpacity>

            {/* Note */}
            <View style={styles.noteRow}>
                <MaterialCommunityIcons
                    name="information-outline"
                    size={14}
                    color={colors.textTertiary}
                    style={{ opacity: 0.6 }}
                />
                <Text style={styles.noteText}>Takes a few seconds</Text>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 32,
        paddingVertical: 40
    },
    iconContainer: {
        width: 96,
        height: 96,
        borderRadius: radius["2xl"],
        borderWidth: 1,
        borderColor: colors.border,
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 24,
        ...shadows.sm
    },
    title: {
        fontSize: 20,
        fontFamily: fonts.bold,
        color: colors.textPrimary,
        marginBottom: 10
    },
    subtitle: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textSecondary,
        textAlign: "center",
        lineHeight: 21,
        maxWidth: 280,
        marginBottom: 32
    },
    btnWrapper: {
        width: "100%",
        maxWidth: 280
    },
    generateBtn: {
        width: "100%",
        paddingVertical: 16,
        paddingHorizontal: 24,
        borderRadius: radius.lg,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        ...shadows.brand
    },
    generateBtnText: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    noteRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginTop: 16
    },
    noteText: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary
    }
});
